/**
 * SharedView - Página pública read-only que carga y muestra un snapshot
 * compartido por otro usuario. Ruta: /share/:id
 *
 * Detecta el pathname `/share/<id>`, hace fetch a /api/share/{id} y renderiza
 * la vista adecuada según `tool`:
 *  - comparator → tabla de precios + score + correlación
 *  - proanalyst → health score + fair value + tips
 *  - analyzer   → mini info + gráfico simple
 *
 * NO requiere autenticación. Si el snapshot no existe o caducó, muestra un
 * mensaje amistoso con CTA a la landing.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShareNetwork, ArrowLeft, CalendarBlank, Eye, WarningCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const COLORS = ['#06b6d4', '#f97316', '#8b5cf6', '#ec4899'];

// ============================================================================
// Sub-vistas por tool
// ============================================================================
const ComparatorView = ({ data }) => {
  const tickers = data?.tickers || [];
  const period = data?.period || '';
  const assetsData = data?.assetsData || {};
  const compositeSuggestion = data?.compositeSuggestion || {};
  const analysis = data?.analysisData || {};
  const risk = data?.riskMetrics || {};
  const technical = data?.technicalIndicators || {};
  const fund = data?.fundamentalData || {};
  const pro = data?.proAnalystData || {};
  const corr = data?.correlationMatrix;

  const cellPct = (v) => {
    if (!Number.isFinite(v)) return <span className="text-gray-500">—</span>;
    const cls = v >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={cls}>{v >= 0 ? '+' : ''}{v.toFixed(2)}%</span>;
  };

  return (
    <div className="space-y-6">
      {/* STICKY TICKER HEADER en móvil (feb 2026):
          en scroll, sale una barra fina con los 4 tickers y su cambio para
          que el usuario nunca pierda el contexto. */}
      <div className="sticky top-[52px] sm:hidden z-30 -mx-4 px-2 py-1.5 bg-[#0a0f1e]/95 backdrop-blur border-b border-gray-800">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {tickers.map((t, i) => {
            const info = assetsData[t]?.info || {};
            const change = info.changePercent || 0;
            return (
              <div key={t} className="flex-shrink-0 px-2 py-1 rounded-md border flex items-center gap-1.5" style={{ borderColor: `${COLORS[i]}55`, background: `${COLORS[i]}10` }}>
                <span className="text-[10px] font-bold" style={{ color: COLORS[i] }}>{t}</span>
                <span className={`text-[10px] font-mono ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cards con precio actual - móvil apilado 2 cols, desktop 4 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {tickers.map((t, i) => {
          const info = assetsData[t]?.info || {};
          const change = info.changePercent || 0;
          return (
            <div key={t} className="p-2 sm:p-3 rounded-xl border" style={{ borderColor: `${COLORS[i]}55`, background: `${COLORS[i]}10` }}>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wider mb-0.5" style={{ color: COLORS[i] }}>Activo {i+1}</div>
              <div className="font-bold text-white text-base sm:text-lg truncate">{t}</div>
              <div className="text-white font-mono text-sm">{(info.currentPrice || 0).toFixed(2)}€</div>
              <div className={`text-[11px] sm:text-xs font-mono font-bold ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(2)}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Sugerencia con score */}
      <section>
        <h3 className="text-sm font-bold text-gold-400 uppercase tracking-wider mb-2">Sugerencia (Score combinado)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {tickers.map((t, i) => {
            const cs = compositeSuggestion[t];
            if (!cs) return <div key={t} className="p-3 rounded-lg border border-gray-700 text-gray-500 text-sm">—</div>;
            return (
              <div key={t} className="p-3 rounded-lg border" style={{ borderColor: `${cs.color}55`, background: `${cs.color}15` }}>
                <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: COLORS[i] }}>{t}</div>
                <div className="font-bold text-sm" style={{ color: cs.color }}>{cs.label}</div>
                <div className="text-xs text-gray-400 mt-1">Score: <span className="font-bold" style={{ color: cs.color }}>{cs.score >= 0 ? '+' : ''}{cs.score.toFixed(0)}</span></div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Rendimiento */}
      <section>
        <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-2">Rendimiento por Periodo</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[400px]">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2 px-2 text-gray-400">Periodo</th>
                {tickers.map((t, i) => <th key={t} className="text-center py-2 px-2 font-bold" style={{ color: COLORS[i] }}>{t}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                { k: 'change_1d', l: '1D' }, { k: 'change_1w', l: '1S' }, { k: 'change_1m', l: '1M' },
                { k: 'change_3m', l: '3M' }, { k: 'change_6m', l: '6M' }, { k: 'change_1y', l: '1A' },
              ].map(({ k, l }) => (
                <tr key={k} className="border-b border-gray-800">
                  <td className="py-1.5 px-2 text-gray-300 font-semibold">{l}</td>
                  {tickers.map(t => <td key={t} className="text-center py-1.5 px-2 font-mono">{cellPct(analysis[t]?.[k])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Técnicos + Riesgo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section>
          <h3 className="text-sm font-bold text-zinc-300 uppercase tracking-wider mb-2">Indicadores Técnicos</h3>
          <div className="space-y-1 text-xs">
            {tickers.map((t, i) => {
              const tech = technical[t];
              return (
                <div key={t} className="flex items-center justify-between p-2 rounded border border-gray-800 bg-black/30">
                  <span className="font-bold" style={{ color: COLORS[i] }}>{t}</span>
                  <div className="flex gap-3">
                    <span className="text-gray-400">RSI: <span className={tech?.rsi > 70 ? 'text-red-400' : tech?.rsi < 30 ? 'text-green-400' : 'text-yellow-300'}>{Number.isFinite(tech?.rsi) ? tech.rsi.toFixed(1) : '—'}</span></span>
                    <span className="text-gray-400">MACD: <span className={tech?.hist >= 0 ? 'text-green-400' : 'text-red-400'}>{Number.isFinite(tech?.hist) ? (tech.hist >= 0 ? '+' : '') + tech.hist.toFixed(3) : '—'}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <section>
          <h3 className="text-sm font-bold text-red-300 uppercase tracking-wider mb-2">Riesgo (1 año)</h3>
          <div className="space-y-1 text-xs">
            {tickers.map((t, i) => {
              const r = risk[t];
              return (
                <div key={t} className="flex items-center justify-between p-2 rounded border border-gray-800 bg-black/30">
                  <span className="font-bold" style={{ color: COLORS[i] }}>{t}</span>
                  <div className="flex gap-3 text-gray-400">
                    <span>Vol: <span className="text-white">{Number.isFinite(r?.volatilityAnnualPct) ? r.volatilityAnnualPct.toFixed(1) + '%' : '—'}</span></span>
                    <span>Sharpe: <span className="text-white">{Number.isFinite(r?.sharpe) ? r.sharpe.toFixed(2) : '—'}</span></span>
                    <span>DD: <span className="text-red-400">{Number.isFinite(r?.maxDrawdownPct) ? r.maxDrawdownPct.toFixed(1) + '%' : '—'}</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Correlación */}
      {corr && corr.tickers?.length >= 2 && (
        <section>
          <h3 className="text-sm font-bold text-gold-300 uppercase tracking-wider mb-2">Matriz de Correlación (90d)</h3>
          <div className="overflow-x-auto">
            <table className="text-xs mx-auto border-collapse">
              <thead>
                <tr>
                  <th></th>
                  {corr.tickers.map((t, i) => <th key={t} className="p-2 text-center font-bold" style={{ color: COLORS[tickers.indexOf(t)] || COLORS[i] }}>{t}</th>)}
                </tr>
              </thead>
              <tbody>
                {corr.tickers.map((t1, i) => (
                  <tr key={t1}>
                    <td className="p-2 font-bold text-right" style={{ color: COLORS[tickers.indexOf(t1)] || COLORS[i] }}>{t1}</td>
                    {corr.tickers.map(t2 => {
                      const c = corr.matrix[t1][t2];
                      let bg = '#334155';
                      if (t1 === t2) bg = '#475569';
                      else if (c > 0.7) bg = `rgba(239,68,68,${0.35 + (c-0.7)*1.5})`;
                      else if (c > 0.3) bg = `rgba(251,191,36,${0.3 + (c-0.3)*0.8})`;
                      else if (c >= 0) bg = `rgba(74,222,128,${0.25 + c*0.5})`;
                      else bg = `rgba(59,130,246,${0.3 + Math.abs(c)*0.6})`;
                      return <td key={t2} style={{ background: bg, minWidth: 60 }} className="p-2 text-center font-mono font-bold text-white">{c.toFixed(2)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="text-[11px] text-gray-500 text-center pt-4">
        Comparador · Periodo: <span className="text-gold-400 font-mono">{period}</span> · Los rendimientos pasados no garantizan resultados futuros.
      </p>
    </div>
  );
};

const ProAnalystView = ({ data }) => {
  const t = data?.ticker;
  const hs = data?.health_score;
  const fv = data?.fair_value;
  const tips = data?.tips || data?.pro_tips || [];
  const altman = data?.altman_z;
  const stars = Number.isFinite(hs?.score) ? Math.round(hs.score / 20) : 0;
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-xs text-gray-400 uppercase tracking-wider">Análisis Pro</div>
        <div className="text-4xl font-bold text-white mt-1">{t}</div>
      </div>

      {hs && (
        <section className="bg-black/30 border border-gold-500/30 rounded-xl p-5 text-center">
          <div className="text-xs text-gold-300 uppercase tracking-wider">Health Score</div>
          <div className="text-5xl font-bold text-white mt-2">{'★'.repeat(stars)}<span className="text-gray-600">{'☆'.repeat(5 - stars)}</span></div>
          <div className="text-2xl font-bold text-gold-400 mt-1">{hs.score?.toFixed(0)}/100</div>
          {hs.grade && <div className="text-gray-400 mt-1">Grado: <span className="text-white font-bold">{hs.grade}</span></div>}
        </section>
      )}

      {fv && (
        <section className="bg-black/30 border border-gold-500/30 rounded-xl p-5">
          <div className="text-xs text-gold-300 uppercase tracking-wider mb-2">Fair Value</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm">Precio Justo</div>
              <div className="text-2xl font-bold text-white font-mono">{Number.isFinite(fv.weighted_avg) ? fv.weighted_avg.toFixed(2) + '$' : '—'}</div>
            </div>
            <div className="text-right">
              <div className="text-gray-400 text-sm">Veredicto</div>
              <div className="text-lg font-bold" style={{ color: fv.verdict === 'undervalued' ? '#00ff6a' : fv.verdict === 'overvalued' ? '#f87171' : '#fbbf24' }}>
                {fv.verdict_label || fv.verdict || '—'}
              </div>
            </div>
          </div>
        </section>
      )}

      {altman && (
        <section className="bg-black/30 border border-emerald-500/30 rounded-xl p-5">
          <div className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Solvencia (Altman Z)</div>
          <div className="text-2xl font-bold text-white font-mono">{Number.isFinite(altman.score) ? altman.score.toFixed(2) : '—'}</div>
          <div className="text-sm text-gray-400 mt-1">{altman.zone_label || altman.zone || ''}</div>
        </section>
      )}

      {tips.length > 0 && (
        <section>
          <div className="text-xs text-zinc-300 uppercase tracking-wider mb-2">Pro Tips</div>
          <ul className="space-y-2">
            {tips.slice(0, 6).map((tip, i) => (
              <li key={i} className="p-3 rounded-lg border border-gray-800 bg-black/30 text-sm text-gray-200">
                {typeof tip === 'string' ? tip : (tip.text || tip.message || JSON.stringify(tip))}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

const AnalyzerView = ({ data }) => {
  const t = data?.ticker;
  const info = data?.info || {};
  const change = info.changePercent || 0;
  const highlights = data?.highlights || [];
  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-xs text-gray-400 uppercase tracking-wider">Análisis Técnico</div>
        <div className="text-4xl font-bold text-white mt-1">{t}</div>
        <div className="text-2xl font-mono text-white mt-2">{(info.currentPrice || 0).toFixed(2)}€</div>
        <div className={`text-lg font-bold font-mono ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </div>
      </div>
      {highlights.length > 0 && (
        <section>
          <div className="text-xs text-gold-300 uppercase tracking-wider mb-2">Highlights</div>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <li key={i} className="p-3 rounded-lg border border-gray-800 bg-black/30 text-sm text-gray-200">
                {typeof h === 'string' ? h : (h.text || JSON.stringify(h))}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

// ============================================================================
// Componente principal
// ============================================================================
const SharedView = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const shareId = React.useMemo(() => {
    const m = window.location.pathname.match(/^\/share\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : null;
  }, []);

  useEffect(() => {
    if (!shareId) { setError('URL inválida'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/share/${shareId}`, { timeout: 12000 });
        if (!cancelled) setSnapshot(res.data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(status === 410 ? 'expired' : status === 404 ? 'not_found' : 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  const goHome = () => { window.location.href = '/'; };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <div className="text-gray-400 text-sm mt-3">Cargando snapshot…</div>
        </div>
      </div>
    );
  }

  if (error) {
    const errMap = {
      expired: { title: 'Enlace caducado', msg: 'Este snapshot compartido ha caducado (30 días). Pídele al remitente que genere uno nuevo.' },
      not_found: { title: 'Snapshot no encontrado', msg: 'El enlace no existe o fue eliminado.' },
      error: { title: 'Error al cargar', msg: 'No se pudo recuperar el snapshot. Prueba de nuevo en unos segundos.' },
    };
    const info = errMap[error] || errMap.error;
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <WarningCircle size={64} className="text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{info.title}</h1>
          <p className="text-gray-400 mb-6">{info.msg}</p>
          <button onClick={goHome} className="px-6 py-2 rounded-lg bg-gold-500 hover:bg-gold-400 text-black font-semibold" data-testid="share-goto-home">
            Ir a Kapital
          </button>
        </div>
      </div>
    );
  }

  const { tool, title, data, created_at, expires_at, views } = snapshot;

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      {/* Barra superior */}
      <header className="sticky top-0 z-40 bg-[#0f172a]/95 backdrop-blur border-b border-gold-500/20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <button onClick={goHome} className="flex items-center gap-2 text-gold-400 hover:text-gold-300 text-sm" data-testid="share-back">
            <ArrowLeft size={16} />
            <span className="font-semibold">Kapital</span>
          </button>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Eye size={12} />{views || 0}</span>
            <span className="hidden sm:flex items-center gap-1"><CalendarBlank size={12} />{fmtDate(created_at)}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6" data-testid="shared-view-root">
        {/* Banner con título + aviso de solo lectura */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-gold-500/10 to-zinc-500/10 border border-gold-500/30">
          <div className="flex items-center gap-2 text-gold-400 text-xs font-semibold uppercase tracking-wider">
            <ShareNetwork size={14} />
            <span>Snapshot compartido — Solo lectura</span>
          </div>
          <h1 className="text-2xl font-bold text-white mt-1">{title || 'Análisis'}</h1>
          <div className="text-xs text-gray-500 mt-1">Caduca el {fmtDate(expires_at)}</div>
        </div>

        {/* Vista según tool */}
        {tool === 'comparator' && <ComparatorView data={data} />}
        {tool === 'proanalyst' && <ProAnalystView data={data} />}
        {tool === 'analyzer' && <AnalyzerView data={data} />}
        {!['comparator', 'proanalyst', 'analyzer'].includes(tool) && (
          <div className="text-gray-400">Tipo de snapshot desconocido: {tool}</div>
        )}

        {/* Footer con CTA */}
        <div className="mt-10 pt-6 border-t border-gray-800 text-center">
          <p className="text-sm text-gray-400 mb-3">¿Te interesa este análisis?</p>
          <button onClick={goHome} className="px-6 py-2 rounded-lg bg-gold-500 hover:bg-gold-400 text-black font-semibold" data-testid="share-cta-signup">
            Prueba Kapital gratis
          </button>
        </div>
      </main>
    </div>
  );
};

export default SharedView;
